// Wrapper around React.lazy that recovers from "Importing a module
// script failed" / ChunkLoadError errors caused by stale tabs after a
// deploy.
//
// Background: each route is in a content-hashed chunk. When a deploy
// lands, old chunk hashes (referenced by JS running in the user's
// open tab) no longer exist on the CDN — clicking into a new route
// tries to dynamically import a file that 404s and the React.lazy()
// rejection bubbles up to the nearest error boundary.
//
// This wrapper catches that failure once and force-reloads — the next
// load picks up the fresh index.html which references the current
// chunks. A sessionStorage flag prevents an infinite reload loop if
// the import is genuinely broken (rather than just a stale-tab issue).
import { lazy, type ComponentType } from 'react'

const RETRY_FLAG_KEY = 'punch.lazyRetried'

function isChunkLoadError(err: unknown): boolean {
  if (!err) return false
  const msg = err instanceof Error ? err.message : String(err)
  const name = err instanceof Error ? err.name : ''
  return (
    name === 'ChunkLoadError' ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /Loading chunk \d+ failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg)
  )
}

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): ReturnType<typeof lazy<T>> {
  return lazy(() =>
    factory().catch(err => {
      if (!isChunkLoadError(err)) throw err
      // Don't reload if we're offline — the reload won't be able to
      // fetch the chunk either, and we'd just thrash. Surface the
      // error to the ErrorBoundary so the user sees the friendly
      // "Something went wrong" page instead of a reload loop.
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        throw err
      }
      const alreadyRetried = sessionStorage.getItem(RETRY_FLAG_KEY) === '1'
      if (alreadyRetried) {
        // We've already force-reloaded in this session and the chunk
        // is still failing — surface the error to the error boundary
        // rather than spin in a reload loop.
        throw err
      }
      sessionStorage.setItem(RETRY_FLAG_KEY, '1')
      window.location.reload()
      // Return a never-resolving promise so the suspense fallback
      // stays mounted while the reload happens.
      return new Promise<{ default: T }>(() => {})
    }),
  )
}

// Call once on successful first paint to clear the retry guard so a
// later (real) deploy can use it again.
export function clearLazyRetryFlag() {
  try { sessionStorage.removeItem(RETRY_FLAG_KEY) } catch {}
}
